// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { TableRow, Loading } from '@carbon/react';
import { number, object, bool, func, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Package, ChevronRight, ChevronLeft, Gift } from 'lucide-react';
import Carousel from 'react-multi-carousel';

import useScreen from 'Hooks/useScreen';
import useAnalytics from 'Hooks/useAnalytics';
import Img from 'Containers/Image/Image';
import { StyledProductListItem } from 'Components/Products/StyledProductItems';
import Price from 'Components/Price/Price';
import AddToCart from 'Components/AddToCart/AddToCart';
import { PROMOTION_BNDL, PROMOTION_PRGF } from 'Constants';

const StyledTableRow = styled(TableRow)`
  &:hover {
    background-color: transparent !important;
  }

  > td {
    padding: 0 0 16px;
    border-top: none;

    .cartPromotionSuggestions {
      width: 90%;
      margin: 0 auto;

      .notice {
        display: flex;
        justify-content: end;
        padding: 4px 0;
        color: #A29D9D;
        font-size: 14px;
      }

      .suggestionsHeader {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        background: #F4F7FC;

        .bold {
          font-weight: 600;
        }

        .green, svg {
          color: #68A765;
        }
      }

      .suggestionsList {
        position: relative;
        background: #F5F5F5;
        padding: 16px;
      }
    }
  }
`;

const StyledPromotionListItem = styled(StyledProductListItem)`
  flex-direction: row;
  min-height: 0;

  &[type='BNDL'] {
    .products {
      top: 12px;
    }
  }

  .products {
    position: relative;
    display: grid;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
    flex-grow: 1;

    .promotionCarousel {
      margin-bottom: 1.5rem;

      + .react-multi-carousel-dot-list {
        z-index: 1;
        
        .react-multi-carousel-dot--active {
          button {
            background-color: #FF8519;
          }
        }

        button {
          border: 0;
          min-width: 0;
          min-height: 0;
          width: 14px;
          height: 14px;
          padding: 0;
          margin: 0 5px;
          border-radius: 14px;
          background-color: #A8A8A8;

          &.react-multi-carousel-dot--active {
            background-color: #FF8519;
          }
        }
      }
    }
  }

  .promoItem {
    &:not(:last-of-type):after {
      display: block;
      position: absolute;
      right: -3px;
      top: 50%;
      transform: translateY(-50%);
      content: '+';
      font-size: 3.5rem;
      font-weight: bold;
      color: ${ props => props.theme.products.colors.inStock };
    }
  }

  .priceContainer {
    border: 0;
    position: relative;

    &:before {
      position: absolute;
      left: -30px;
      top: 50%;
      padding-left: 10px;
      transform: translateY(-50%);
      display: block;
      content: '=';
      font-size: 3.5rem;
      line-height: 3rem;
      color: ${ props => props.theme.products.colors.inStock };
      background-color: #F5F5F5;
    }

    .addToCart {
      font-size: 1rem;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    flex-direction: column;

    .products {
      grid-auto-flow: row;
      width: 100%;

      .promoItem {
        margin-bottom: 45px;
        padding: 0; 

        &:not(:last-of-type):after {
          right: 50%;
          top: calc(100% - 10px);
          transform: translate(50%, 0);
        }
      }
    }

    .priceContainer {
      flex-direction: column;
      align-items: center;
      padding: 0;
      width: 100%;
      min-width: 100%;

      &:before {
        position: static;
        transform: translate(0);
        text-align: center;
      }
    }
  }
`;

const StyledPromoProduct = styled.div`
  position: relative;
  padding: 0 15px;
  display: flex;
  align-items: center;

  .preview {
    top: 0 !important;
    left: 0;
    min-height: 90px !important;
    min-width: 90px !important;
    max-width: 90px !important;
    max-height: 90px !important;
    margin: 0 10px 0 0 !important;
    border: 0 !important;
    position: relative;
    overflow: hidden;

    .giftIcon {
      background-color: rgba(255, 255, 255, 0.9);
      padding: 3px 5px;
      position: absolute;
      top: 0;
      left: 0;
    }

    svg {
      max-width: 55px !important;
    }

    .image {
      margin: 0 auto;
      width: auto;
      height: auto;
      max-width: 90px;
      max-height: 90px;
    }
  }

  .details {
    font-size: 0.875rem;
    flex-direction: column !important;
    overflow: hidden;
    padding-right: 10px;

    .title {
      font-size: 0.875rem !important;
      line-height: 1.25rem !important;
      color: ${ props => props.theme.colors.darkGray };
      margin: 0;
      padding: 0;
    }

    .quantity {
      font-weight: bold;
      margin: 5px 0 2px;
    }

    .price {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      margin: 0;
      font-size: 1rem;
      font-weight: bold;
      color: ${ props => props.theme.colors.default };

      .oldPrice {
        margin-right: 5px;
        font-size: 0.875rem;
        line-height: 1.375rem;
        font-weight: normal;
      }

      .currentPrice {
        font-size: 1rem;
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    .preview {
      button {
        display: none;
      }
    }
  }
`;

const StyledButtonGroup = styled.div`
  position: absolute;
  top: -33px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 16px;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    width: 30px;
    height: 30px;
    border-radius: 2px;
    border: 1px solid #E2E2E2;
    color: #3A3A3A;
    cursor: pointer;

    &.disable {
      color: #E2E2E2;
    }
  }
`;

const ButtonGroup = ({ next, previous, ...rest }) => {
  const { carouselState: { currentSlide, totalItems, slidesToShow } } = rest;
  const isLastSlide = currentSlide >= totalItems - slidesToShow;
  const analytics = useAnalytics();

  return (
    <StyledButtonGroup>
      <button
        type="button"
        aria-label="Previous slide"
        className={ currentSlide === 0 ? 'disable' : '' }
        onClick={ () => {
          previous();
          analytics.track('promotion_suggestions_slider_previous_click');
        } }
      >
        <ChevronLeft size={ 24 } />
      </button>
      <button
        type="button"
        aria-label="Next slide"
        className={ isLastSlide ? 'disable' : '' }
        onClick={ () => {
          next();
          analytics.track('promotion_suggestions_slider_next_click');
        } }
      >
        <ChevronRight size={ 24 } />
      </button>
    </StyledButtonGroup>
  );
};

ButtonGroup.propTypes = {
  next: func.isRequired,
  previous: func.isRequired,
};

// custom dots to prevent form submission
const CustomDot = ({ onClick, active }) => (
  <button
    type="button"
    className={ active ? 'react-multi-carousel-dot--active' : '' }
    onClick={ e => {
      e.preventDefault();
      onClick();
    } }
    aria-label="Go to slide"
  />
);

CustomDot.propTypes = {
  onClick: func,
  active: bool,
};

const CartPromotionSuggestions = ({
  colSpan,
  product,
  onAddPromoToCart,
  theme,
  loading,
  currency,
}) => {
  const { t } = useTranslation();
  const { width } = useScreen();

  const appliedPromotions = (product.availablePromotions || []).filter(promotion => {
    const hasOutOfStock = (promotion.products || []).some(
      prod => prod.minQuantity > prod.stockValue || !prod.stockValue
    );
    return !hasOutOfStock;
  });

  const hasMultiplePromotions = appliedPromotions.length > 1;

  if (appliedPromotions.length === 0) {
    return null;
  }

  const renderProduct = (promoProduct, promo, productIndex) => (
    <StyledPromoProduct
      key={ `${promo.promotionId}_${promoProduct.productId || promoProduct.mainProductId || promoProduct.slug || 'product'}_${productIndex}` }
      className="promoItem"
      theme={ theme }
    >
      <div className="preview">
        { promo.type === PROMOTION_PRGF && !promoProduct.isMainProduct && (
          <div className="giftIcon">
            <Gift />
          </div>
        ) }
        <Link
          to={ `/products/${ promoProduct.slug }/pd/${ promoProduct.productId }` }
          state={ {
            component: 'product_promotions',
            source: 'promotion_product_image',
            clickedOn: 'image',
            productId: promoProduct.productId
          } }
        >
          <Img
            className="image"
            src={ promoProduct?.thumbnailPictureUrl }
            unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
            loader={ <Loading active withOverlay={ false } small /> }
            loading="lazy"
            alt={ product.name }
          />
        </Link>
      </div>
      <div className="details">
        <Link
          className="title"
          to={ `/products/${ promoProduct.slug }/pd/${ promoProduct.productId }` }
          state={ {
            component: 'product_promotions',
            source: 'promotion_product_title',
            clickedOn: 'title',
            productId: promoProduct.productId
          } }
        >
          { promoProduct.title }
        </Link>
        <p className="quantity">
          { promoProduct.quantity }
          &nbsp;
          { promoProduct.quantity === 1
            ? t('products.product')
            : t('products.products')
          }
        </p>
        {
          promoProduct.totalPrice === 0
            ? <p className="price">{ t('products.free') }</p>
            : (
              <Price
                price={ promoProduct.price * promoProduct.quantity }
                promoPrice={ promoProduct.totalPrice }
                withVat={ false }
                displayVatInfo
                currency={ currency }
              />
            )
        }
      </div>
    </StyledPromoProduct>
  );

  const promotionItems = appliedPromotions.map(promotion => {
    const mainPromoProduct = promotion.products
      .find(promoProd => promoProd.mainProductId === (product.id || product.productId));

    return (
      <StyledPromotionListItem
        key={ promotion.promotionId }
        theme={ theme }
        type={ promotion.type }
        className="promotionContainer"
      >
        <div className="products">
          { promotion?.promotionType === PROMOTION_BNDL && (promotion.products || []).length > 3
            ? (
              <Carousel
                className="promotionCarousel"
                ssr
                deviceType="desktop"
                arrows={ false }
                showDots
                customDot={ <CustomDot /> }
                renderDotsOutside
                itemClass="promoItem"
                infinite={ false }
                responsive={ {
                  desktop: {
                    breakpoint: { max: 3000, min: 1540 },
                    items: 2
                  },
                  tablet: {
                    breakpoint: { max: 1539, min: 540 },
                    items: 1
                  },
                  mobile: {
                    breakpoint: { max: 539, min: 0 },
                    items: 1
                  }
                } }
              >
                { (promotion.products || []).map((prod, productIndex) => renderProduct(prod, promotion, productIndex)) }
              </Carousel>
            )
            : (promotion.products || []).map((prod, productIndex) => renderProduct(prod, promotion, productIndex))
          }
        </div>
        <div className="priceContainer">
          <Price
            price={ promotion.totalPrice }
            promoPrice={ promotion.totalPromoPrice }
            discountAmount={ promotion.totalPrice - promotion.totalPromoPrice }
            withVat={ false }
            displayVatInfo
            currency={ currency }
          />
          <AddToCart
            id={ promotion.promotionId }
            size="lg"
            loading={ loading === promotion.promotionId }
            disabled={ loading === promotion.promotionId }
            onClick={ () => onAddPromoToCart({
              ...(mainPromoProduct || product),
              // hardcode to not show confirmation modal for promotions since there are no supplier products here
              hasPrice: true,
              id: (
                mainPromoProduct?.mainProductId
                || mainPromoProduct?.productId
                || product.mainProductId
                || product.productId
                || product.id
              ),
              promotionId: promotion.promotionId,
            }) }
            trackingContext={{
              location: 'cart_product_promotions',
            }}
            showSelector={ false }
          />
        </div>
      </StyledPromotionListItem>
    );
  });

  return (
    <StyledTableRow className="cartPromotionSuggestionsRow">
      <td colSpan={ colSpan }>
        <div className="cartPromotionSuggestions">
          <div className="notice">
            <span>*{ t('order.promotionSuggestionsNotice') }</span>
          </div>
          <div className="suggestionsHeader">
            <Package />
            <p dangerouslySetInnerHTML={ { __html: t('order.promotionSuggestionsInfo') } } />
          </div>
          <div className="suggestionsList">
            { hasMultiplePromotions
              ? (
                <Carousel
                  className="promotionSuggestionsSlider"
                  ssr
                  customButtonGroup={ <ButtonGroup /> }
                  deviceType="desktop"
                  showDots={ false }
                  autoPlay
                  infinite
                  autoPlaySpeed={ 5000 }
                  renderButtonGroupOutside
                  arrows={ false }
                  slidesToSlide={ 1 }
                  removeArrowOnDeviceType={ ['tablet', 'mobile'] }
                  shouldResetAutoplay={ width > 767 }
                  responsive={ {
                    desktop: {
                      breakpoint: { max: 3000, min: 1540 },
                      items: 1
                    },
                    tablet: {
                      breakpoint: { max: 1539, min: 767 },
                      items: 1
                    },
                    mobile: {
                      breakpoint: { max: 767, min: 0 },
                      items: 1,
                    }
                  } }
                >
                  { promotionItems }
                </Carousel>
              )
              : promotionItems
            }
          </div>
        </div>
      </td>
    </StyledTableRow>
  );
};

CartPromotionSuggestions.propTypes = {
  colSpan: number.isRequired,
  product: object.isRequired,
  onAddPromoToCart: func.isRequired,
  theme: object.isRequired,
  loading: bool,
  currency: string.isRequired,
};

export default CartPromotionSuggestions;
